import { useApp } from "@/App";
import { useFetch } from "@/lib/hooks";
import { api } from "@/lib/api";
import { CATEGORY_LABELS, CONDITION_LABELS, CATEGORY_ICONS } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ReputationBadge } from "@/components/ReputationBadge";
import {
	ArrowLeft, ArrowLeftRight, Tag, User, Calendar, DollarSign,
	Cpu, Shirt, BookOpen, Sofa, Dumbbell, Gamepad2, Music,
	Palette, Wrench, ChefHat, Trees, Gem, Package,
} from "lucide-react";

const iconMap: Record<string, React.FC<{ className?: string }>> = {
	Cpu, Shirt, BookOpen, Sofa, Dumbbell, Gamepad2, Music,
	Palette, Wrench, ChefHat, Trees, Gem, Package,
};

export function ItemDetailPage({ itemId }: { itemId: string }) {
	const { navigate, currentUserId } = useApp();
	const { data: item, loading } = useFetch(() => api.items.get(itemId), [itemId]);
	const { data: owner } = useFetch(
		() => (item ? api.users.get(item.ownerId) : Promise.resolve(null)),
		[item?.ownerId]
	);

	if (loading) {
		return (
			<div className="max-w-4xl mx-auto space-y-6">
				<div className="h-8 bg-muted rounded w-32 animate-pulse" />
				<div className="grid md:grid-cols-2 gap-6">
					<div className="aspect-square bg-muted rounded-lg animate-pulse" />
					<div className="space-y-4">
						<div className="h-8 bg-muted rounded w-3/4 animate-pulse" />
						<div className="h-4 bg-muted rounded w-full animate-pulse" />
						<div className="h-4 bg-muted rounded w-2/3 animate-pulse" />
					</div>
				</div>
			</div>
		);
	}

	if (!item) {
		return (
			<div className="text-center py-12">
				<p className="text-muted-foreground">Item not found.</p>
				<Button variant="link" onClick={() => navigate("/browse")}>Back to browse</Button>
			</div>
		);
	}

	const IconComponent = iconMap[CATEGORY_ICONS[item.category]] || Package;
	const isOwner = item.ownerId === currentUserId;

	return (
		<div className="max-w-4xl mx-auto space-y-6">
			<Button variant="ghost" size="sm" onClick={() => navigate("/browse")}>
				<ArrowLeft className="h-4 w-4 mr-1.5" />
				Back to browse
			</Button>

			<div className="grid md:grid-cols-2 gap-8">
				<div className="aspect-square bg-muted rounded-xl flex items-center justify-center">
					<div className="flex flex-col items-center gap-3 text-muted-foreground/40">
						<IconComponent className="h-24 w-24" />
						<span className="text-sm font-medium">{CATEGORY_LABELS[item.category]}</span>
					</div>
				</div>

				<div className="space-y-5">
					<div>
						<div className="flex items-start justify-between gap-3">
							<h1 className="text-2xl font-bold tracking-tight">{item.title}</h1>
						</div>
						<div className="flex items-center gap-2 mt-2">
							<Badge variant="secondary">{CATEGORY_LABELS[item.category]}</Badge>
							<Badge variant="outline">{CONDITION_LABELS[item.condition]}</Badge>
						</div>
					</div>

					<p className="text-muted-foreground leading-relaxed">{item.description}</p>

					<div className="grid grid-cols-2 gap-4">
						<div className="flex items-center gap-2 text-sm">
							<DollarSign className="h-4 w-4 text-muted-foreground" />
							<span className="text-muted-foreground">Est. Value:</span>
							<span className="font-semibold">${item.estimatedValue}</span>
						</div>
						<div className="flex items-center gap-2 text-sm">
							<Calendar className="h-4 w-4 text-muted-foreground" />
							<span className="text-muted-foreground">Listed:</span>
							<span className="font-medium">{item.createdAt}</span>
						</div>
					</div>

					{item.tags.length > 0 && (
						<div className="flex flex-wrap gap-1.5">
							<Tag className="h-3.5 w-3.5 text-muted-foreground mt-0.5" />
							{item.tags.map((tag) => (
								<Badge key={tag} variant="outline" className="text-xs">
									{tag}
								</Badge>
							))}
						</div>
					)}

					{item.wantedCategories.length > 0 && (
						<div>
							<p className="text-xs font-medium text-muted-foreground mb-1.5">Looking to trade for:</p>
							<div className="flex flex-wrap gap-1.5">
								{item.wantedCategories.map((cat) => (
									<Badge key={cat} variant="secondary" className="text-xs">
										{CATEGORY_LABELS[cat]}
									</Badge>
								))}
							</div>
						</div>
					)}

					<Separator />

					{!isOwner && (
						<Button size="lg" className="w-full" onClick={() => navigate(`/trade/${item.ownerId}`)}>
							<ArrowLeftRight className="h-4 w-4 mr-2" />
							Propose a Swap
						</Button>
					)}

					{isOwner && (
						<p className="text-sm text-muted-foreground text-center py-2">
							This is your item
						</p>
					)}
				</div>
			</div>

			{owner && (
				<Card>
					<CardHeader className="pb-3">
						<CardTitle className="text-base">Listed by</CardTitle>
					</CardHeader>
					<CardContent>
						<div
							className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
							onClick={() => navigate(`/profile/${owner.id}`)}
						>
							<div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
								<User className="h-5 w-5 text-primary" />
							</div>
							<div>
								<p className="font-semibold text-sm">{owner.displayName}</p>
								<p className="text-xs text-muted-foreground">@{owner.username}</p>
							</div>
							<div className="ml-auto">
								<ReputationBadge reputation={owner.reputation} totalSwaps={owner.totalSwaps} />
							</div>
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
}
